import { useId } from 'react'
import { RadioGroup, RadioGroupItem } from './RadioGroup'
import { cn } from '@/utils/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

export interface Option<T extends string> {
    value: T
    label: React.ReactNode
  }

const radioGroupTabVariants = cva(
    'flex p-0.5 bg-ground box-border',
    {
        variants: {
            size: {
                sm: 'h-7 rounded-2',
                md: 'h-9 rounded-2.5',
                lg: 'h-11 rounded-3',
                mdIcon: 'h-9 rounded-2.5'
            }
        },
        defaultVariants: {
            size: 'md'
        }
    }
)

const radioGroupTabLabelVariants = cva(
    'flex flex-1 justify-center items-center shrink h-full transition-200 cursor-pointer',
    {
        variants: {
            size: {
                sm: 'px-2 rounded-1.5',
                md: 'px-3.5 rounded-2',
                lg: 'px-5 rounded-2.5',
                mdIcon: 'min-w-8 rounded-2'
            }
        },
        defaultVariants: {
            size: 'md'
        }
    }
)

export interface RadioGroupTabProps<T extends string = string>
    extends VariantProps<typeof radioGroupTabVariants> {
    options: { value: T; label: React.ReactNode }[]
    value: T
    onValueChange: (value: T) => void
    className?: string
}

const RadioGroupTab = <T extends string = string>({
    options,
    value,
    onValueChange,
    size = 'md',
    className
}: RadioGroupTabProps<T>) => {
    const generatedId = useId()

    if (!options || options.length === 0) {
        return null
    }

    return (
        <div className={cn(radioGroupTabVariants({ size }), className)}>
            <RadioGroup
                value={value}
                onValueChange={onValueChange}
                className="group relative flex items-center w-full gap-0 text-body-md"
            >
                {options.map((option) => (
                    <label
                        key={option.value}
                        htmlFor={`${generatedId}-${option.value}`}
                        className={cn(
                            radioGroupTabLabelVariants({ size }),
                            value !== option.value && 'text-tertiary hover:bg-overlay-1',
                            value === option.value && 'text-brand-primary bg-surface'
                        )}
                    >
                        {option.label}
                        <RadioGroupItem
                            value={option.value}
                            id={`${generatedId}-${option.value}`}
                            className="sr-only"
                        />
                    </label>
                ))}
            </RadioGroup>
        </div>
    )
}

export default RadioGroupTab
